import { resolveApiBaseUrl } from './api-base.js';

/**
 * Public checkout config — everything the SPA needs to render before there's
 * a session. Driven entirely by the tenant's CheckoutStrategy.display block
 * plus tenant branding. No PII, no secrets.
 */
export interface CheckoutConfigDto {
  tenant: {
    id: string;
    name: string;
    branding: {
      logoUrl: string;
      primaryColor: string;
      fontFamily: string;
      supportEmail: string;
      supportPhone: string;
    };
    defaultCurrency: string;
    defaultLanguage: string;
    /** Drives the SPA's optional rrweb recorder. */
    replay: ReplayConfigClientHint;
  };
  strategy: {
    id: string;
    name: string;
    version: number;
    display: {
      eyebrow: Record<string, string>;
      title: Record<string, string>;
      subtitle: Record<string, string>;
      referenceHelpText?: Record<string, string>;
      submitLabel?: Record<string, string>;
      payLabel?: Record<string, string>;
      payFootnote?: Record<string, string>;
      trustBadges: {
        icon: string;
        title: Record<string, string>;
        subtitle: Record<string, string>;
      }[];
      footerBrand?: Record<string, string>;
    };
    methodEligibility: {
      paymentMethod: string;
      enabled: boolean;
      tier?: 'primary' | 'secondary' | 'hidden';
      displayOrder?: number;
      label?: Record<string, string>;
      caption?: Record<string, string>;
      icon?: string;
      processingCallout?: {
        text: Record<string, string>;
        icon?: string;
        tone?: 'info' | 'success' | 'warning' | 'neutral';
      };
      processingDetail?: Record<string, string>;
    }[];
    /** Per-method currency constraints (constrained methods only). The SPA
     *  hides a method when the active currency isn't in its list. */
    methodCurrencies?: Record<string, string[]>;
    consent: {
      id: string;
      label: Record<string, string>;
      required: boolean;
      category: string;
      termsVersion: string;
      termsUrl: string;
    }[];
    incentives: { id: string; enabled: boolean; label?: string; appliesTo?: string[] }[];
    contextualProcessing?: {
      enabled: boolean;
      heading?: Record<string, string>;
      emptyStateText?: Record<string, string>;
    };
    blocks: CheckoutBlockDto[];
    /**
     * Operator-defined URL query-param → form-field mappings. The Lookup
     * form reads these at mount and seeds its initial state. Empty / missing
     * means no pre-fill.
     */
    queryParamMappings: { param: string; field: CheckoutFormFieldId; note?: string }[];
    /**
     * Per-field declarative validation. Empty / missing keys mean no
     * operator-authored validation for that input.
     */
    fieldValidators?: Partial<Record<CheckoutFormFieldId, FieldValidator>>;
    /**
     * Operator-authored checkout form fields. When present + non-empty the
     * Lookup form renders these instead of the legacy hardcoded inputs.
     * Mirrors `FormFieldDefinition` in `@upg/core/types`.
     */
    formFields?: FormFieldDefinitionDto[];
    /**
     * Operator-authored checkout flow graph. When present, the SPA hands
     * off to the `CheckoutFlowRunner` and walks this graph step-by-step.
     * When absent / null, the SPA falls back to today's hardcoded
     * `Lookup → ConfirmPanel → HPP` path. Mirrors `CheckoutFlow` in
     * `@upg/core/types` — keep field names in lockstep.
     */
    checkoutFlow?: CheckoutFlowDto;
  };
}

/** Mirrors `FormFieldDefinition` in @upg/core/types. */
export interface FormFieldDefinitionDto {
  id: string;
  label: Record<string, string>;
  type: 'text' | 'email' | 'tel' | 'number' | 'select' | 'radio' | 'checkbox';
  required?: boolean;
  placeholder?: Record<string, string>;
  helpText?: Record<string, string>;
  options?: { value: string; label: Record<string, string> }[];
  validator?: FieldValidator;
  defaultValue?: string;
  inputMap?: string;
  order?: number;
}

/** Mirrors `CheckoutNodeKind` in @upg/core/types. */
export type CheckoutNodeKindDto =
  | 'trigger'
  | 'page'
  | 'integration'
  | 'bedrock_prompt'
  | 'decision'
  | 'expression'
  | 'redirect_hpp'
  | 'result'
  // Sprint P — `subflow` placed alphabetically after `result`.
  | 'subflow'
  | 'wait';

/** Mirrors `CheckoutSubflowConfig` in @upg/core/types — Sprint P. */
export interface CheckoutSubflowConfigDto {
  flowId: string;
  inputMap?: Record<string, string>;
  outputMap?: Record<string, string>;
}

/** Mirrors `CheckoutExpressionConfig`. */
export interface CheckoutExpressionConfigDto {
  source: string;
  outputs?: Record<string, string>;
}

/** What the page's submit button does. Mirrors `CheckoutPageSubmitAction`. */
export type CheckoutPageSubmitActionDto = 'advance' | 'create_session' | 'cancel';

/** Mirrors `CheckoutPageSubmit`. */
export interface CheckoutPageSubmitDto {
  label?: Record<string, string>;
  action: CheckoutPageSubmitActionDto;
}

/**
 * Mirrors `CheckoutPageConfig`. The block-canvas fields (`blocks`,
 * `formFieldIds`, `submit`) are the new shape; the legacy `layout` enum
 * is kept optional so an unmigrated DDB row still parses. The runner
 * funnels every page through `legacyPageToBlocks` so downstream code
 * sees the new shape uniformly.
 */
export interface CheckoutPageConfigDto {
  name?: string;
  blocks?: CheckoutBlockDto[];
  formFieldIds?: (string | '*')[];
  submit?: CheckoutPageSubmitDto;
  preAdvance?: { validatorId: string };
  // Legacy (deprecated)
  layout?: 'lookup' | 'confirm' | 'result' | 'custom';
  title?: Record<string, string>;
  subtitle?: Record<string, string>;
  showFieldIds?: string[];
  submitLabel?: Record<string, string>;
}

/** Mirrors `CheckoutIntegrationConfig`. */
export interface CheckoutIntegrationConfigDto {
  validatorId: string;
  outputMapping?: Record<string, string>;
  onFailure?: 'stop' | 'take_failure_edge';
}

/** Mirrors `CheckoutDecisionCondition`. */
export interface CheckoutDecisionConditionDto {
  leftToken: string;
  comparator:
    | 'equals'
    | 'not_equals'
    | 'contains'
    | 'starts_with'
    | 'greater_than'
    | 'less_than'
    | 'in'
    | 'not_in'
    | 'is_empty'
    | 'is_not_empty';
  rightValue?: string;
}

/** Mirrors `CheckoutDecisionConfig`. */
export interface CheckoutDecisionConfigDto {
  condition: CheckoutDecisionConditionDto;
}

/**
 * Mirrors `CheckoutWaitConfig` in @upg/core/types. Sprint G — the
 * Wait/Poll Task node. `waitPageBlocks` is rendered to the shopper via
 * `BlockRenderer` while the server polls.
 */
export interface CheckoutWaitConfigDto {
  validatorId?: string;
  pollUrl?: string;
  intervalMs: number;
  maxAttempts: number;
  successWhen: 'status_2xx' | 'jsonpath_equals';
  successJsonPath?: string;
  successEqualsValue?: string;
  waitPageBlocks: CheckoutBlockDto[];
}

/** Mirrors `CheckoutNode`. */
export interface CheckoutNodeDto {
  id: string;
  kind: CheckoutNodeKindDto;
  position?: { x: number; y: number };
  page?: CheckoutPageConfigDto;
  integration?: CheckoutIntegrationConfigDto;
  decision?: CheckoutDecisionConfigDto;
  expression?: CheckoutExpressionConfigDto;
  wait?: CheckoutWaitConfigDto;
  // Sprint P — present iff `kind === 'subflow'`.
  subflow?: CheckoutSubflowConfigDto;
}

/** Mirrors `CheckoutEdge`. */
export interface CheckoutEdgeDto {
  from: string;
  to: string;
  label?: string;
}

/** Mirrors `CheckoutFlow`. */
export interface CheckoutFlowDto {
  nodes: CheckoutNodeDto[];
  edges: CheckoutEdgeDto[];
  description?: string;
}

/** Mirrors `CheckoutFormFieldId` in @upg/core/types. */
export type CheckoutFormFieldId = 'referenceNumber' | 'email' | 'name';

/** Mirrors `FieldValidator` in @upg/core/types. */
export interface FieldValidator {
  required?: boolean;
  min?: number;
  max?: number;
  pattern?: string;
  patternMessage?: string;
  format?: 'free_text' | 'email' | 'phone' | 'numeric' | 'alphanumeric' | 'iban' | 'uppercase_alpha';
  transform?: 'none' | 'trim' | 'uppercase' | 'lowercase';
  customMessage?: string;
}

/** Bits of replayConfig the checkout SPA needs to decide whether to record. */
export interface ReplayConfigClientHint {
  enabled: boolean;
  captureDom: boolean;
}

/**
 * Page-builder block. Same union as `@upg/core` CheckoutBlock; mirrored here
 * so the checkout package stays free of a runtime dep on core.
 */
export type CheckoutBlockDto =
  | {
      id: string;
      type: 'heading';
      text: Record<string, string>;
      level: 'h1' | 'h2' | 'h3';
      align?: 'left' | 'center' | 'right';
    }
  | { id: string; type: 'paragraph'; text: Record<string, string>; size?: 'sm' | 'md' | 'lg' }
  | {
      id: string;
      type: 'image';
      src: string;
      alt: Record<string, string>;
      maxWidth?: number;
      align?: 'left' | 'center' | 'right';
    }
  | { id: string; type: 'divider' }
  | {
      id: string;
      type: 'callout';
      tone: 'info' | 'warning' | 'success' | 'danger';
      icon?: string;
      text: Record<string, string>;
      /** Render `text` as sanitized markdown (e.g. the LLM weather tip). */
      scopeMarkdown?: boolean;
    }
  | { id: string; type: 'spacer'; size: 'sm' | 'md' | 'lg' }
  | { id: string; type: 'lookup-form' }
  | { id: string; type: 'method-tiles' }
  | { id: string; type: 'consents' }
  | { id: string; type: 'summary' }
  | { id: string; type: 'pay-button' }
  // === Sprint F mirrors — keep in sync with @upg/core's CheckoutBlock ===
  | { id: string; type: 'form-fields'; fieldIds: (string | '*')[] }
  | { id: string; type: 'markdown'; source: Record<string, string>; scopeMarkdown?: boolean }
  | { id: string; type: 'risk-badge'; show: 'decision' | 'score' | 'narration' | 'all' }
  | {
      id: string;
      type: 'progress-stepper';
      stepsFromFlow?: boolean;
      steps?: Array<{ id: string; label: Record<string, string> }>;
      currentStepId?: string;
    };

export async function loadCheckoutConfig(
  args: { tenant: string; strategy: string } | { link: string },
): Promise<CheckoutConfigDto> {
  // Opaque link path: the tenant is resolved server-side from the token and is
  // never placed in any request URL. Legacy tenant+strategy path preserved.
  const query =
    'link' in args
      ? `link=${encodeURIComponent(args.link)}`
      : `tenant=${encodeURIComponent(args.tenant)}&strategy=${encodeURIComponent(args.strategy)}`;
  const url = `${baseUrl}/v1/checkout/config?${query}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j?.error?.message ?? `Could not load checkout config (${res.status})`);
  }
  return (await res.json()) as CheckoutConfigDto;
}

/** Pull a localized string from a `Record<locale, string>` shape with `en` fallback. */
export function pickLocale(field: Record<string, string> | undefined, locale: string): string {
  if (!field) return '';
  return field[locale] ?? field.en ?? Object.values(field)[0] ?? '';
}

export interface CheckoutSessionDto {
  id: string;
  payment_intent_id?: string;
  amount: { value: string; currency: string };
  expires_at: string;
  status: 'OPEN' | 'CONFIRMED' | 'EXPIRED' | 'CANCELLED';
  methods: { payment_method: string }[];
  consents: {
    id: string;
    label: Record<string, string>;
    required: boolean;
    category: string;
    terms_url: string;
    terms_version: string;
  }[];
  display: {
    applicant_name?: string;
    visa_class?: string;
    expires_at?: string;
  };
}

export interface ConfirmResponse {
  payment_intent_id: string;
  status: string;
  redirect_url: string | null;
  external_ids: Record<string, string>;
}

export const baseUrl = resolveApiBaseUrl(import.meta.env);

export async function createSession(body: {
  /**
   * Effective tenant id (config.tenant.id). Sent as `X-Tenant-Id` because
   * `/v1/checkout/sessions` is a pre-auth public route: without it,
   * `tenantOf(req)` falls through to the API default tenant — absent in cloud —
   * and create_session 412s 'Tenant not found'. Same fix as the risk session
   * (bd y5v); the tenant stays out of the URL (opaque /c/<token> path).
   */
  tenant?: string;
  strategy_name: string;
  validator_inputs: Record<string, string>;
  shopper?: { email?: string; name?: string };
}): Promise<CheckoutSessionDto> {
  const { tenant, ...payload } = body;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Idempotency-Key': crypto.randomUUID(),
  };
  if (tenant) headers['X-Tenant-Id'] = tenant;
  const res = await fetch(`${baseUrl}/v1/checkout/sessions`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j?.error?.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as CheckoutSessionDto;
}

/**
 * Durable runner-state record returned by `getFlowState`. Mirrors
 * `FlowStateRecord` in `@upg/persistence`; kept inline here so the checkout
 * package stays free of a runtime dep on persistence.
 */
export interface FlowStateRecordDto {
  tenantId: string;
  riskSessionId: string;
  currentNodeId: string | null;
  scope: Record<string, unknown>;
  updatedAt: string;
}

/**
 * Read the durable runner state for a (tenant, riskSession). Returns
 * `{ state: null }` when no row exists — caller falls back to the trigger
 * node as the entry point.
 *
 * Fire-on-mount; fail-soft. A network blip on hydrate should not block the
 * shopper — the caller treats any error as "no resumable state".
 */
export async function getFlowState(args: {
  tenant: string;
  sessionId: string;
  riskToken: string;
}): Promise<FlowStateRecordDto | null> {
  const url = `${baseUrl}/v1/checkout/flow/state?session_id=${encodeURIComponent(args.sessionId)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'X-Tenant-Id': args.tenant,
      'X-Replay-Token': args.riskToken,
    },
    credentials: 'include',
  });
  if (!res.ok) return null;
  const j = (await res.json()) as { state?: FlowStateRecordDto | null };
  return j.state ?? null;
}

/**
 * Persist the runner state. Fire-and-forget — the runner calls this on every
 * advance and never awaits it on the shopper-critical path.
 */
export async function putFlowState(args: {
  tenant: string;
  sessionId: string;
  riskToken: string;
  currentNodeId: string | null;
  scope: Record<string, unknown>;
}): Promise<void> {
  const url = `${baseUrl}/v1/checkout/flow/state`;
  await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': args.tenant,
      'X-Replay-Token': args.riskToken,
    },
    credentials: 'include',
    body: JSON.stringify({
      session_id: args.sessionId,
      current_node_id: args.currentNodeId,
      scope: args.scope,
    }),
  });
}

/**
 * Sprint G — one wait-tick. The runner posts on mount (first tick — seeds
 * the server's counter) and on every `setTimeout` fire thereafter.
 *
 * Response shapes (server is authoritative):
 *   - `{ status: 'awaiting', nextPollAt, intervalMs, attempt }` — the
 *     server is still polling; the runner re-arms a timer to call back.
 *   - `{ status: 'done', nextNodeId }` — success criterion met; the
 *     runner advances to the resolved successor.
 *   - `{ status: 'timeout', nextNodeId, failureCode: 'WAIT_TIMEOUT' }` —
 *     `maxAttempts` exhausted; runner takes the `failure-WAIT_TIMEOUT`
 *     typed edge or the wildcard `failure`.
 */
export interface WaitTickResponse {
  status: 'awaiting' | 'done' | 'timeout' | 'failed';
  nextPollAt?: string | null;
  attempt?: number | null;
  intervalMs?: number;
  nextNodeId?: string | null;
  failureCode?: string;
  message?: string;
}

export async function tickWait(args: {
  tenant: string;
  strategy: string;
  sessionId: string;
  nodeId: string;
  riskToken: string;
}): Promise<WaitTickResponse> {
  const url = `${baseUrl}/v1/checkout/flow/wait-tick`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': args.tenant,
      'X-Replay-Token': args.riskToken,
    },
    credentials: 'include',
    body: JSON.stringify({
      session_id: args.sessionId,
      strategy_name: args.strategy,
      node_id: args.nodeId,
    }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error((j as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as WaitTickResponse;
}

/**
 * Response shape from `/v1/checkout/flow/task`. The server dispatches
 * the operator-authored `expression` / `bedrock_prompt` Task node and
 * returns the scope patch + the next node id (already typed-failure-edge
 * aware so the runner doesn't need its own edge-picker for these kinds).
 */
export interface TaskRunResponse {
  ok: boolean;
  failureCode?: string;
  message?: string;
  scopePatch?: Record<string, unknown>;
  output?: Record<string, unknown>;
  nextNodeId: string | null;
}

/**
 * POST a `expression` or `bedrock_prompt` Task to the server for
 * dispatch via `NODE_EXECUTORS`. The runner sends the current scope so
 * the executor can template against it (Bedrock) or read values (expression).
 */
export async function runTask(args: {
  tenant: string;
  strategy: string;
  sessionId: string;
  nodeId: string;
  scope: Record<string, unknown>;
  riskToken: string;
}): Promise<TaskRunResponse> {
  const url = `${baseUrl}/v1/checkout/flow/task`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': args.tenant,
      'X-Replay-Token': args.riskToken,
    },
    credentials: 'include',
    body: JSON.stringify({
      session_id: args.sessionId,
      strategy_name: args.strategy,
      node_id: args.nodeId,
      scope: args.scope,
    }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error((j as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as TaskRunResponse;
}

/** Terminal PI statuses where polling for a redirect should stop + fail. */
const TERMINAL_FAILURE_STATUSES = new Set(['DECLINED', 'CANCELLED', 'TIMED_OUT', 'FAILED']);

/**
 * Poll the session for the provider redirect URL produced by the async
 * authorization (bd asb). In cloud the API runs the AuthorizationSM under Step
 * Functions, so confirm returns `AUTHORIZING` with no `redirect_url`; the
 * SaferPay HostedPaymentPage URL lands on the PI moments later. We poll the AWS
 * API (with X-Tenant-Id) until it appears — never falling back to localhost or
 * an in-process path.
 */
export async function pollForRedirectUrl(
  sessionId: string,
  tenant: string,
  opts: { attempts?: number; intervalMs?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<string> {
  const attempts = opts.attempts ?? 30;
  const intervalMs = opts.intervalMs ?? 1000;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  for (let i = 0; i < attempts; i += 1) {
    const res = await fetch(`${baseUrl}/v1/checkout/sessions/${sessionId}`, {
      headers: { 'X-Tenant-Id': tenant },
      credentials: 'include',
    });
    if (res.ok) {
      const j = (await res.json()) as { payment_status?: string | null; redirect_url?: string | null };
      if (j.redirect_url) return j.redirect_url;
      if (j.payment_status && TERMINAL_FAILURE_STATUSES.has(j.payment_status)) {
        throw new Error(`Payment ${j.payment_status.toLowerCase()} before reaching the payment page.`);
      }
    }
    if (i < attempts - 1) await sleep(intervalMs);
  }
  throw new Error('Timed out waiting for the payment page. Please try again.');
}

export async function confirmSession(
  sessionId: string,
  body: {
    payment_method: string;
    shopper?: { email?: string; name?: string };
    consents: { id: string; termsVersion: string; acceptedAt: string }[];
  },
  riskSessionToken?: string,
  /**
   * Effective tenant id (config.tenant.id). Sent as `X-Tenant-Id` for the same
   * reason createSession does: `/v1/checkout/sessions/:id/confirm` is a public
   * route, so without it the server's `tenantOf(req)` falls back to its default
   * tenant and looks up the session under the wrong tenant → "Session not
   * found" → the SaferPay HPP handoff never happens (bd confirm-tenant-header).
   */
  tenant?: string,
): Promise<ConfirmResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Idempotency-Key': crypto.randomUUID(),
  };
  if (riskSessionToken) headers['X-Risk-Session-Token'] = riskSessionToken;
  if (tenant) headers['X-Tenant-Id'] = tenant;
  const res = await fetch(`${baseUrl}/v1/checkout/sessions/${sessionId}/confirm`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j?.error?.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as ConfirmResponse;
}
